import { getUsersPreferencesPipeSeriesQueryKey } from "@imi/api-client/index.js";
import { PlusIcon } from "@imi/icons/PlusIcon.js";
import { UndoIcon } from "@imi/icons/UndoIcon.js";
import { Button } from "@imi/ui/components/Button/Button.js";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogCloseButton,
  DialogHeader,
} from "@imi/ui/components/Dialog/Dialog.js";
import { Heading } from "@imi/ui/components/Heading/Heading.js";
import { selectSubmitOnEnter } from "@src/shared/helpers/form";
import { useQueryClient } from "@tanstack/react-query";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { Form, Link } from "react-router";
import { z } from "zod/v4";
import { DialogErrorFallback } from "../../components/DialogErrorFallback";
import { DialogLoadingFallback } from "../../components/DialogLoadingFallback";
import { useRouteDialogPath } from "../../hooks/useRouteDialogPath";
import { useRouteDialogProps } from "../../hooks/useRouteDialogProps";
import { PipeSelect } from "./components/PipeSelect";
import { PipeSeriesSelect } from "./components/PipeSeriesSelect";
import { usePipeSeriesForm } from "./hooks/usePipeSeriesForm";
import styles from "./SettingsPipeSeriesDialog.module.css";

const argsSchema = z.object({
  defaultPipeSeries: z.string().optional(),
  ignorePipe: z.boolean().optional(),
});

export function SettingsPipeSeriesDialog() {
  const { t } = useTranslation("settings");
  const routeDialogProps = useRouteDialogProps({ argsSchema });
  const { getRouteDialogPath } = useRouteDialogPath();

  return (
    <Dialog {...routeDialogProps} className={styles.SettingsPipesSeries}>
      <DialogHeader className={styles.DialogHeader}>
        <Heading as="h3" subTitle={t("pipeSeries.subTitle")}>
          {t("pipeSeries.title")}
        </Heading>
        <Button
          as={Link}
          className={styles.AddPipesSeries}
          to={getRouteDialogPath("settings.pipeSeries.add")}
          variant="ghost"
        >
          <PlusIcon />
          {t("pipeSeries.actions.add")}
        </Button>
      </DialogHeader>
      <ErrorBoundary FallbackComponent={DialogErrorFallback}>
        <Suspense fallback={<DialogLoadingFallback />}>
          <SettingsPipeSeriesDialogContent />
        </Suspense>
      </ErrorBoundary>
      <DialogCloseButton>{t("pipeSeries.actions.close")}</DialogCloseButton>
    </Dialog>
  );
}

function SettingsPipeSeriesDialogContent() {
  const { t } = useTranslation("settings");
  const routeDialogProps = useRouteDialogProps({ argsSchema });
  const queryClient = useQueryClient();

  const { defaultValue, definitions, form, submitting } = usePipeSeriesForm({
    defaultSeriesId: routeDialogProps.args?.defaultPipeSeries,
    onSubmit: () => {
      queryClient.invalidateQueries({
        queryKey: getUsersPreferencesPipeSeriesQueryKey(),
      });
      routeDialogProps.onOpenChange(false);
    },
  });

  return (
    <>
      <DialogBody className={styles.DialogBody}>
        <Form
          className={styles.Form}
          id={form.formId}
          onKeyDown={(e) => selectSubmitOnEnter(e, submitting)}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit(e);
          }}
        >
          <fieldset>
            <PipeSeriesSelect definitions={definitions} form={form} />
            {!routeDialogProps.args?.ignorePipe && <PipeSelect form={form} />}
          </fieldset>
        </Form>
      </DialogBody>
      <DialogActions className={styles.DialogActions}>
        <Button
          className={styles.ResetButton}
          disabled={submitting}
          onClick={() => {
            form.reset(defaultValue);
          }}
          variant="outline"
        >
          <UndoIcon />
          {t("pipeSeries.actions.reset")}
        </Button>
        <DialogClose disabled={submitting} variant="outline">
          {t("pipeSeries.actions.cancel")}
        </DialogClose>
        <Button form={form.formId} loading={submitting} type="submit">
          {t("pipeSeries.actions.confirm")}
        </Button>
      </DialogActions>
    </>
  );
}
